import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { ServiceAdviceBlock } from "@/components/ServiceAdviceBlock";
import { ServiceCautionBlock } from "@/components/ServiceCautionBlock";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { EDUCATION_FAQ_CATEGORIES } from "@/content/education-corpus/faq-defs";

export const Route = createFileRoute("/en/education/faq/")({
  loader: async (): Promise<{ faqs: { q: string; a: string }[] }> => {
    const m = await import("@/content/education-corpus");
    return { faqs: m.EDUCATION_FAQ_EN };
  },
  component: EducationFaqHubEn,
  head: ({ loaderData }) => {
    const faqs = loaderData?.faqs ?? [];
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `Education Document Certification — ${faqs.length} FAQs`,
        description:
          "Certifying degrees and transcripts from Thailand: Thai MFA legalisation, certified translation, the Apostille from 28 February 2027, WES and ENIC-NARIC credential evaluation, Teachers' Council licensing and destination-embassy attestation.",
        path: "/en/education/faq",
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/education/faq" }],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Education Documents FAQ", url: `${site}/en/education/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/education/faq")) },
      ],
    };
  },
});

function EducationFaqHubEn() {
  const { faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>Education Documents FAQ</span>
        </nav>
        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Degrees, transcripts and education document certification — {faqs.length} FAQs
        </h1>
        <p data-speakable="true" className="speakable-summary mb-6 text-muted-foreground">
          Answers follow the practice of the Legalisation Division of the Thai Department of Consular Affairs
          (consular.mfa.go.th), the Ministry of Education (moe.go.th), MHESI (mhesi.go.th), the Teachers' Council of
          Thailand (ksp.or.th) and the HCCH Apostille Convention, which enters into force for Thailand on 28 February
          2027. General information, not an official determination.
        </p>
        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>ภาษาไทย:</strong>{" "}
          <a href="/education/faq" className="text-primary underline">รับรองเอกสารการศึกษา →</a>
        </div>
        <section aria-label="Categories" className="mb-8">
          <h2 className="mb-3 text-lg font-semibold">Browse by topic</h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {EDUCATION_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/education/faq/${c.slug}`} className="rounded-lg border bg-card p-3 text-sm transition hover:border-primary hover:shadow-sm">
                <div className="mb-1 font-semibold">{c.titleEn}</div>
                <div className="line-clamp-2 text-xs text-muted-foreground">{c.descEn}</div>
              </a>
            ))}
          </div>
        </section>
        <div className="space-y-4">
          {faqs.map((item: { q: string; a: string }, i: number) => (
            <details key={i} className="rounded-lg border bg-card p-4" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                {item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
        <ServiceAdviceBlock
          locale="en"
          items={[
            "Ask your institution for English-language degree certificates and transcripts first; the Legalisation Division of the Department of Consular Affairs (consular.mfa.go.th) certifies documents issued by the competent authority, and English originals remove a translation step.",
            "If the document is in Thai, have it translated into English with name spellings that match your passport exactly, then submit the translation together with the original for MFA certification.",
            "For foreign-issued qualifications used in Thailand, certify them in the issuing country first, then have them certified by the Royal Thai Embassy or Consulate, or use an Apostille once the Convention enters into force for Thailand on 28 February 2027.",
            "If you need your qualification recognised for study or employment in Thailand, check the current guidance of the Ministry of Education (moe.go.th) and MHESI (mhesi.go.th) on equivalency certificates before starting the certification chain.",
            "Teaching roles also involve the Teachers' Council of Thailand (ksp.or.th) licence requirements alongside the work permit, and the document set differs from a study application.",
            "Confirm the exact document list, steps and timelines with our staff by phone, LINE or email before shipping anything, because requirements vary by institution and destination country.",
          ]}
        />
        <ServiceCautionBlock
          locale="en"
          items={[
            "Submitting only scans or plain photocopies — legalisation requires the original or an institution-certified copy.",
            "Name spellings in the translation that differ from the passport or civil registration records.",
            "Unsealed transcripts or student-printed copies, which credential evaluators abroad usually reject.",
            "Assuming an Apostille is already available for Thailand; until 28 February 2027 the MFA legalisation plus destination-embassy certification route still applies.",
            "Completing only one certification layer when the destination country also requires certification by its own embassy in Thailand.",
            "Leaving no buffer before an admission deadline or start date if the institution has to reissue documents.",
          ]}
        />
        <section className="mt-10 rounded-lg border bg-card p-5">
          <h2 className="mb-2 text-lg font-bold">Talk to our team</h2>
          <p className="text-sm text-muted-foreground">Ask about documents, steps and timelines by phone, LINE or email, Mon-Sat 9:00-18:00.</p>
          <a href="/contact" className="mt-3 inline-block rounded-md border px-4 py-2 text-sm font-semibold hover:bg-muted">Contact us</a>
        </section>
        <FaqSourceNote topic="education" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}